import { BrowserBroadcast } from '@adapters/broadcast/browserBroadcast';
import { TaggingMetricMapper } from '@adapters/tagging/taggingMetricMapper';
import { FactoryProvider, InjectionToken, ValueProvider } from '@angular/core';
import { TaggingTracker } from '@core/model/domain/taggingTracker';
import { NotificationSwitcher } from '@core/model/framework/notifications/notificationSwitcher';
import { GetBanks } from '@core/usecase/getBanks';
import { GetWebauthUrl } from '@core/usecase/getWebauthUrl';
import { SaveWebauthBankConnection } from '@core/usecase/saveWebauthBankConnection';
import { WaitOnWebauthNotification } from '@core/usecase/waitOnWebauthNotification';
import { GetTerms } from '@core/usecase/getTerms';
import { SendConsent } from '@core/usecase/sendConsent';
import { AbortCase } from '@core/usecase/abortCase';
import { FailCase } from '@core/usecase/failCase';
import { SERVICE_ID } from '@di/serviceId';
import { Kernel } from '@di/kernel';
import { GetAccounts } from '@core/usecase/getAccounts';
import { AuthenticateToBankWithCredentials } from '@core/usecase/authenticateToBankWithCredentials';
import { WaitOnBankAccountAnalysisCompletion } from '@core/usecase/waitOnBankAccountAnalysisCompletion';
import { GetBankById } from '@core/usecase/getBankById';
import { GetAngularDependenciesService } from '../../app/getAngularDependencies.service';

export const isTrackingEnabledToken = new InjectionToken<boolean>('isTrackingEnabledToken');
export const taggingTrackerToken = new InjectionToken<TaggingTracker>('taggingTrackerToken');
export const taggingMetricMapperToken = new InjectionToken<TaggingMetricMapper>(
  'Token : taggingMetricMapper - debug'
);
export const browserBroadcastToken = new InjectionToken<BrowserBroadcast>(
  'Token : browserBroadcastToken - debug'
);
export const loggerToken = new InjectionToken<BrowserBroadcast>('Token : loggerToken - debug');

export const coreDependenciesToAngularProviderMapper = (kernel: Kernel): ValueProvider[] => {
  return [
    {
      provide: SaveWebauthBankConnection,
      useValue: kernel.getByIdentifier(SaveWebauthBankConnection),
    },
    {
      provide: GetBanks,
      useValue: kernel.getByIdentifier(GetBanks),
    },
    {
      provide: GetWebauthUrl,
      useValue: kernel.getByIdentifier(GetWebauthUrl),
    },
    {
      provide: GetTerms,
      useValue: kernel.getByIdentifier(GetTerms),
    },
    {
      provide: GetAccounts,
      useValue: kernel.getByIdentifier(GetAccounts),
    },
    {
      provide: SendConsent,
      useValue: kernel.getByIdentifier(SendConsent),
    },
    {
      provide: AbortCase,
      useValue: kernel.getByIdentifier(AbortCase),
    },
    {
      provide: FailCase,
      useValue: kernel.getByIdentifier(FailCase),
    },
    {
      provide: WaitOnWebauthNotification,
      useValue: kernel.getByIdentifier(WaitOnWebauthNotification),
    },
    {
      provide: WaitOnBankAccountAnalysisCompletion,
      useValue: kernel.getByIdentifier(WaitOnBankAccountAnalysisCompletion),
    },
    {
      provide: NotificationSwitcher,
      useValue: kernel.getByIdentifier(NotificationSwitcher),
    },
    {
      provide: AuthenticateToBankWithCredentials,
      useValue: kernel.getByIdentifier(AuthenticateToBankWithCredentials),
    },
    {
      provide: GetBankById,
      useValue: kernel.getByIdentifier(GetBankById),
    },
    {
      provide: isTrackingEnabledToken,
      useValue: kernel.getByIdentifier(SERVICE_ID.isTrackingEnabled),
    },
    {
      provide: browserBroadcastToken,
      useValue: kernel.getByIdentifier(SERVICE_ID.browserBroadcast),
    },
    {
      provide: loggerToken,
      useValue: kernel.getByIdentifier(SERVICE_ID.logger),
    },
  ];
};

export const bindAngularDependenciesToCoreAndMapToAngularProvider = (kernel: Kernel): FactoryProvider[] => {
  return [
    {
      provide: taggingTrackerToken,
      useFactory: (getAngularDependenciesService: GetAngularDependenciesService) => {
        if (kernel.isBound(SERVICE_ID.taggingTracker)) {
          throw new Error(
            `${SERVICE_ID.taggingTracker} already bound ! This function should not be called twice`
          );
        }

        const tagCommanderService = getAngularDependenciesService.getTagCommanderService();
        return kernel.bindTaggingTracker(tagCommanderService);
      },
      deps: [GetAngularDependenciesService],
    },
    {
      provide: taggingMetricMapperToken,
      useFactory: (getAngularDependenciesService: GetAngularDependenciesService) => {
        if (kernel.isBound(SERVICE_ID.taggingMetricMapper)) {
          throw new Error(
            `${SERVICE_ID.taggingMetricMapper} already bound ! This function should not be called twice`
          );
        }

        const tagCommanderMetricMapper = getAngularDependenciesService.getTagCommanderMetricMapper();
        return kernel.bindTaggingMetricMapper(tagCommanderMetricMapper);
      },
      deps: [GetAngularDependenciesService],
    },
  ];
};
